import { useState, useEffect, useCallback, useMemo } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useTablesQuery, useColumnsQuery, useIndexesQuery } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import type { Index } from '@/types/compatibility'
import {
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface CreateIndexViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  table?: string // Pre-selected table when creating index from specific table
  onIndexCreated: () => void
}

interface IndexColumn {
  name: string
  order: 'ASC' | 'DESC'
}

interface TableData {
  name?: string
  type?: string
  dataType?: string
  schema?: string
  engine?: string
  rows?: number
  size?: string
}

interface ColumnData {
  name?: string
  type?: string
  dataType?: string
}

export function CreateIndexView({ connection, database, schema, table, onIndexCreated }: CreateIndexViewProps) {
  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'
  
  const [indexName, setIndexName] = useState('')
  const [selectedTable, setSelectedTable] = useState(table || '')
  const [indexType, setIndexType] = useState<'INDEX' | 'UNIQUE' | 'BTREE' | 'HASH' | 'GIST' | 'SPGIST' | 'GIN' | 'BRIN'>(
    connection.type === 'mysql' ? 'INDEX' : 'BTREE'
  )
  const [isUnique, setIsUnique] = useState(false)
  const [columns, setColumns] = useState<IndexColumn[]>([{ name: '', order: 'ASC' }])
  const [whereClause, setWhereClause] = useState('')
  const [concurrent, setConcurrent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [indexNameError, setIndexNameError] = useState('')
  const [tableError, setTableError] = useState('')
  const [columnsError, setColumnsError] = useState('')
  const [whereClauseError, setWhereClauseError] = useState('')
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  
  const { showToast } = useToast()
  
  const { data: tablesResponse, error: tablesError, isLoading: tablesLoading } = useTablesQuery(
    connection.id,
    database,
    schema,
    { 
      enabled: true,
      databaseType: connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'
    }
  )

  // Fetch columns for the selected table
  const { data: columnsResponse, error: columnsQueryError, isLoading: columnsLoading } = useColumnsQuery(
    connection.id,
    database,
    selectedTable,
    schema,
    { 
      enabled: !!selectedTable,
      databaseType: connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'
    }
  )

  // Fetch existing indexes for validation
  const { data: indexesResponse } = useIndexesQuery(
    connection.id,
    database,
    schema,
    { enabled: true, limit: 1000, databaseType: connection.type }
  )

  const tables = useMemo(() => {
    const data = tablesResponse?.data
    return Array.isArray(data) ? data as TableData[] : []
  }, [tablesResponse?.data])
  
  const tableColumns = useMemo(() => {
    const data = columnsResponse
    if (!Array.isArray(data)) return []
    
    // Convert DatabaseColumn objects to ColumnData format expected by the component
    return data.map(col => ({
      name: col.name || col.column_name,
      type: col.type || col.data_type,
      dataType: col.data_type
    })) as ColumnData[]
  }, [columnsResponse])
  
  const existingIndexes: Index[] = useMemo(() => {
    const data = indexesResponse?.data
    return Array.isArray(data) ? data as Index[] : []
  }, [indexesResponse?.data])

  // Validate table selection
  const validateTable = useCallback((tableName: string): string => {
    if (!tableName.trim()) {
      return 'Please select a table'
    }
    
    // Don't validate if tables are still loading
    if (tablesLoading) {
      return ''
    }
    
    // Don't validate if no tables have been loaded (might be an error condition)
    if (tables.length === 0) {
      return ''
    }
    
    // Check if table exists in the available tables
    const tableExists = tables.some((table) => table.name === tableName)
    if (!tableExists) {
      return `Selected table "${tableName}" does not exist`
    }
    
    return ''
  }, [tablesLoading, tables])

  // Validate columns configuration
  const validateColumns = useCallback((cols: IndexColumn[]): string => {
    if (cols.length === 0) {
      return 'At least one column is required'
    }

    // Check if at least one column is selected
    const selectedColumns = cols.filter(col => col.name && col.name.trim())
    if (selectedColumns.length === 0) {
      return 'Please select at least one column for the index'
    }

    // Check for duplicate columns
    const columnNames = selectedColumns.map(col => col.name.toLowerCase())
    const uniqueColumns = new Set(columnNames)
    if (columnNames.length !== uniqueColumns.size) {
      return 'Duplicate columns are not allowed in the same index'
    }

    // Only validate column existence if we have a selected table and columns are loaded
    if (selectedTable && tableColumns.length > 0 && !columnsLoading) {
      const availableColumns = tableColumns
        .filter(col => col.name)
        .map(col => col.name!.toLowerCase())
      const invalidColumns = columnNames.filter(colName => !availableColumns.includes(colName))
      if (invalidColumns.length > 0) {
        return `Invalid columns: ${invalidColumns.join(', ')}`
      }
    }

    return ''
  }, [selectedTable, tableColumns, columnsLoading])

  // Validate WHERE clause for partial indexes
  const validateWhereClause = (clause: string): string => {
    if (!clause.trim()) {
      return '' // WHERE clause is optional
    }

    // Basic SQL injection prevention
    const dangerousPatterns = [
      /DROP\s+/i,
      /DELETE\s+/i,
      /INSERT\s+/i,
      /UPDATE\s+/i,
      /CREATE\s+/i,
      /ALTER\s+/i,
      /EXEC\s+/i,
      /EXECUTE\s+/i,
      /--/,
      /\/\*/,
      /\*\//,
      /;.*$/
    ]

    for (const pattern of dangerousPatterns) {
      if (pattern.test(clause)) {
        return 'WHERE clause contains potentially unsafe SQL'
      }
    }

    // Check for balanced parentheses
    const openParens = (clause.match(/\(/g) || []).length
    const closeParens = (clause.match(/\)/g) || []).length
    if (openParens !== closeParens) {
      return 'Unbalanced parentheses in WHERE clause'
    }

    // Validate maximum length
    if (clause.length > 1000) {
      return 'WHERE clause is too long (maximum 1000 characters)'
    }

    return ''
  }

  // Validate entire form
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    const nameError = validateIndexName(indexName)
    if (nameError) errors.indexName = nameError

    const tableError = validateTable(selectedTable)
    if (tableError) errors.table = tableError

    const columnsError = validateColumns(columns)
    if (columnsError) errors.columns = columnsError

    if (isDatabasePostgreSQL) {
      const whereError = validateWhereClause(whereClause)
      if (whereError) errors.whereClause = whereError
    }

    // Validate index type compatibility
    if (isUnique && indexType === 'GIN') {
      errors.indexType = 'GIN indexes cannot be unique'
    }

    if (indexType === 'HASH' && columns.some(col => col.order === 'DESC')) {
      errors.indexType = 'Hash indexes do not support DESC ordering'
    }

    // MySQL specific validations
    if (isDatabaseMySQL && ['BTREE', 'HASH', 'GIN', 'GIST', 'SPGIST', 'BRIN'].includes(indexType)) {
      errors.indexType = 'MySQL only supports INDEX and UNIQUE types'
    }

    // PostgreSQL specific validations
    if (isDatabasePostgreSQL && ['INDEX', 'UNIQUE'].includes(indexType)) {
      errors.indexType = 'PostgreSQL uses BTREE, HASH, GIN, GIST, SPGIST, or BRIN types'
    }

    return errors
  }
  const validateIndexName = useCallback((name: string): string => {
    if (!name.trim()) {
      return 'Index name is required'
    }

    const trimmedName = name.trim()

    // Check for SQL reserved words and invalid characters
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      return 'Index name must start with a letter or underscore and contain only letters, numbers, and underscores'
    }

    // Database-specific validations
    if (connection.type === 'mysql') {
      // MySQL specific validations
      if (trimmedName.length > 64) {
        return 'Index name is too long (MySQL maximum: 64 characters)'
      }
      
      // Check for MySQL reserved words (common ones)
      const mysqlReservedWords = ['index', 'primary', 'unique', 'key', 'table', 'column', 'select', 'insert', 'update', 'delete', 'from', 'where', 'order', 'group', 'by']
      if (mysqlReservedWords.includes(trimmedName.toLowerCase())) {
        return `"${trimmedName}" is a MySQL reserved word and cannot be used as an index name`
      }
    } else {
      // PostgreSQL specific validations
      if (trimmedName.length > 63) {
        return 'Index name is too long (PostgreSQL maximum: 63 characters)'
      }
      
      // Check for PostgreSQL reserved words (common ones)
      const pgReservedWords = ['index', 'primary', 'unique', 'table', 'column', 'select', 'insert', 'update', 'delete', 'from', 'where', 'order', 'group', 'by', 'create', 'drop', 'alter']
      if (pgReservedWords.includes(trimmedName.toLowerCase())) {
        return `"${trimmedName}" is a PostgreSQL reserved word and cannot be used as an index name`
      }
      // Generic validation for other databases
      if (trimmedName.length > 63) {
        return 'Index name is too long (maximum 63 characters)'
      }
    }

    // Check if index name already exists
    const normalizedName = trimmedName.toLowerCase()
    const indexExists = existingIndexes.some(index => {
      const existingName = (index.name || '').toLowerCase()
      return existingName === normalizedName
    })

    if (indexExists) {
      return 'An index with this name already exists'
    }

    // Check for conflicting names on the same table
    if (selectedTable) {
      const tableIndexExists = existingIndexes.some(index => {
        const existingName = (index.name || '').toLowerCase()
        const indexTable = index.table
        return existingName === normalizedName && indexTable === selectedTable
      })

      if (tableIndexExists) {
        return `An index named "${trimmedName}" already exists on table "${selectedTable}"`
      }
    }

    return ''
  }, [connection.type, existingIndexes, selectedTable])

  // Handle table selection with validation
  const handleTableChange = (newTable: string) => {
    setSelectedTable(newTable)
    const error = validateTable(newTable)
    setTableError(error)
    
    // Reset columns when table changes
    setColumns([{ name: '', order: 'ASC' }])
    setColumnsError('')
    
    // Clear form errors to allow revalidation
    setFormErrors({})
    
    // Generate suggested index name only if not already customized
    if (newTable && (!indexName || indexName.startsWith('idx_'))) {
      const suggestedName = `idx_${newTable}_`
      setIndexName(suggestedName)
      // Validate the suggested name
      const nameError = validateIndexName(suggestedName)
      setIndexNameError(nameError)
    }
  }

  // Handle column changes with validation
  const handleColumnChange = (index: number, field: keyof IndexColumn, value: string | number) => {
    const newColumns = Array.from(columns)
    newColumns[index] = { ...newColumns[index], [field]: value }
    setColumns(newColumns)
    
    // Validate columns
    const error = validateColumns(newColumns)
    setColumnsError(error)
    
    // Clear form errors to allow revalidation
    setFormErrors({})
  }

  // Handle adding new column
  const handleAddColumn = () => {
    const newColumns = [...columns, { name: '', order: 'ASC' as const }]
    setColumns(newColumns)
    const error = validateColumns(newColumns)
    setColumnsError(error)
    
    // Clear form errors to allow revalidation
    setFormErrors({})
  }

  // Handle removing column
  const handleRemoveColumn = (index: number) => {
    if (columns.length > 1) {
      const newColumns = columns.filter((_, i) => i !== index)
      setColumns(newColumns)
      const error = validateColumns(newColumns)
      setColumnsError(error)
      
      // Clear form errors to allow revalidation
      setFormErrors({})
    }
  }

  // Handle WHERE clause changes with validation
  const handleWhereClauseChange = (newClause: string) => {
    setWhereClause(newClause)
    const error = validateWhereClause(newClause)
    setWhereClauseError(error)
    
    // Clear form errors to allow revalidation
    setFormErrors({})
  }

  // Handle index type changes with validation
  const handleIndexTypeChange = (newType: string) => {
    setIndexType(newType as 'BTREE' | 'HASH' | 'GIN' | 'GIST' | 'SPGIST' | 'BRIN')
    // Revalidate form to check for type-specific constraints
    const errors = validateForm()
    setFormErrors(errors)
  }

  // Handle unique checkbox changes
  const handleUniqueChange = (isUniqueChecked: boolean) => {
    setIsUnique(isUniqueChecked)
    // Revalidate form to check for unique-specific constraints
    const errors = validateForm()
    setFormErrors(errors)
  }
  const handleIndexNameChange = (newName: string) => {
    setIndexName(newName)
    const error = validateIndexName(newName)
    setIndexNameError(error)
    
    // Clear form errors to allow revalidation
    setFormErrors({})
  }

  // Revalidate all fields when dependencies change
  useEffect(() => {
    if (indexName) {
      const error = validateIndexName(indexName)
      setIndexNameError(error)
    }
    
    if (selectedTable) {
      const error = validateTable(selectedTable)
      setTableError(error)
    }
    
    // Only validate columns if we have columns and either no table selected or table columns are loaded
    if (columns.length > 0 && (!selectedTable || !columnsLoading)) {
      const error = validateColumns(columns)
      setColumnsError(error)
    }
    
    if (isDatabasePostgreSQL && whereClause) {
      const error = validateWhereClause(whereClause)
      setWhereClauseError(error)
    }
  }, [selectedTable, existingIndexes, indexName, columns, whereClause, tables, tableColumns, columnsLoading, isDatabasePostgreSQL, validateColumns, validateIndexName, validateTable])

  // Pre-select table if provided and generate suggested index name
  useEffect(() => {
    if (table && table !== selectedTable) {
      setSelectedTable(table)
      // Generate a suggested index name based on table
      const suggestedName = `idx_${table}_`
      setIndexName(suggestedName)
      // Validate the suggested name
      const error = validateIndexName(suggestedName)
      setIndexNameError(error)
    }
  }, [table, existingIndexes, tables, selectedTable, validateIndexName])

  // Reset columns when table changes (but not on initial load if table is pre-selected)
  useEffect(() => {
    // Only reset columns if we have a selected table and it's not the initial pre-selection
    if (selectedTable && selectedTable !== table) {
      setColumns([{ name: '', order: 'ASC' }])
    }
  }, [selectedTable, table])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate entire form
    const errors = validateForm()
    setFormErrors(errors)
    
    // Update individual error states
    setIndexNameError(errors.indexName || '')
    setTableError(errors.table || '')
    setColumnsError(errors.columns || '')
    setWhereClauseError(errors.whereClause || '')
    
    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      const [firstError] = Object.values(errors)
      showToast('error', firstError)
      return
    }

    setIsSubmitting(true)
    
    try {
      const validColumns = columns.filter(col => col.name && col.name.trim())
      
      // Format columns with ordering information embedded in column names
      // Since the API only accepts string arrays, we embed ordering in SQL syntax
      const formattedColumns = validColumns.map(col => {
        // For DESC ordering, append DESC to column name (SQL syntax)
        // For ASC ordering, use just the column name (ASC is default)
        if (col.order === 'DESC') {
          return `${col.name} DESC`
        }
        return col.name
      })
      
      // Map frontend index types to API-compatible types
      const apiIndexType: 'BTREE' | 'HASH' | 'GIST' | 'SPGIST' | 'GIN' | 'BRIN' | undefined = 
        (indexType === 'INDEX' || indexType === 'UNIQUE') ? undefined : indexType

      await AgentProxyService.createIndex(connection.id, database, {
        index_name: indexName.trim(),
        table_name: selectedTable,
        columns: formattedColumns,
        index_type: apiIndexType,
        unique: isUnique || indexType === 'UNIQUE',
        schema_name: schema
      })

      showToast('success', `Index "${indexName}" created successfully`)
      onIndexCreated()
    } catch (error) {
      console.error('Failed to create index:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create index'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">📇</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Index
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new database index to improve query performance
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Index Name *
                  </label>
                  <input
                    type="text"
                    value={indexName}
                    onChange={(e) => handleIndexNameChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      indexNameError 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="idx_example_name"
                    required
                  />
                  {indexNameError && (
                    <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      {indexNameError}
                    </div>
                  )}
                  {indexName && !indexNameError && existingIndexes.length > 0 && (
                    <div className="mt-1 text-sm text-green-600 dark:text-green-400">
                      ✓ Index name is available
                    </div>
                  )}
                  {selectedTable && existingIndexes.length > 0 && (
                    <div className="mt-2">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                          Show existing indexes on {selectedTable} ({existingIndexes.filter(idx => idx.table === selectedTable).length})
                        </summary>
                        <div className="mt-1 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                          {existingIndexes
                            .filter(idx => idx.table === selectedTable)
                            .map((idx, i) => (
                              <div key={i} className="text-xs text-gray-500 dark:text-gray-400 py-1">
                                • {idx.name} 
                                {idx.primary && <span className="ml-1 text-yellow-600">(PRIMARY)</span>}
                                {idx.unique && !idx.primary && <span className="ml-1 text-green-600">(UNIQUE)</span>}
                                {idx.columns && idx.columns.length > 0 && (
                                  <span className="ml-1 text-gray-400">
                                    [{idx.columns.map((col, colIndex) => {
                                      // Handle the API format which has name and ascending properties
                                      if (typeof col === 'object' && 'name' in col) {
                                        return `${col.name}${col.ascending === false ? '↓' : ''}`
                                      }
                                      // For non-object columns, just show the column index as fallback
                                      return `column_${colIndex}`
                                    }).join(', ')}]
                                  </span>
                                )}
                              </div>
                            ))}
                          {existingIndexes.filter(idx => idx.table === selectedTable).length === 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 py-1">No existing indexes on this table</div>
                          )}
                        </div>
                      </details>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Table *
                  </label>
                  <select
                    value={selectedTable}
                    onChange={(e) => handleTableChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      tableError 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    required
                    disabled={tablesLoading}
                  >
                    <option value="">
                      {tablesLoading ? 'Loading tables...' : 'Select a table'}
                    </option>
                    {tables
                      .filter(table => table.name)
                      .map((table) => (
                        <option key={table.name} value={table.name}>
                          {table.name}
                        </option>
                      ))}
                  </select>
                  {tableError && (
                    <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      {tableError}
                    </div>
                  )}
                  {tablesError && (
                    <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      Error loading tables: {tablesError.message || 'Unknown error'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Index Type
                  </label>
                  <select
                    value={indexType}
                    onChange={(e) => handleIndexTypeChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.indexType 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {isDatabaseMySQL ? (
                      <>
                        <option value="INDEX">INDEX (Default)</option>
                        <option value="UNIQUE">UNIQUE</option>
                      </>
                    ) : (
                      <>
                        <option value="BTREE">B-Tree (Default)</option>
                        <option value="HASH">Hash</option>
                        <option value="GIN">GIN</option>
                        <option value="GIST">GiST</option>
                        <option value="SPGIST">SP-GiST</option>
                        <option value="BRIN">BRIN</option>
                      </>
                    )}
                  </select>
                  {formErrors.indexType && (
                    <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      {formErrors.indexType}
                    </div>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isUnique"
                    checked={isUnique}
                    onChange={(e) => { handleUniqueChange(e.target.checked) }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="isUnique" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Unique Index
                  </label>
                  {formErrors.indexType && isUnique && (
                    <div className="ml-2 text-sm text-red-600 dark:text-red-400">
                      ({formErrors.indexType})
                    </div>
                  )}
                </div>

                {isDatabasePostgreSQL && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="concurrent"
                      checked={concurrent}
                      onChange={(e) => { setConcurrent(e.target.checked) }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="concurrent" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Create Concurrently
                    </label>
                    <div className="ml-2 group relative">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="invisible group-hover:visible absolute z-10 w-64 p-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg -top-2 left-6">
                        Creates the index without blocking concurrent INSERTs, UPDATEs, or DELETEs on the table. Takes longer but doesn't lock the table.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Columns */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Index Columns
                </h3>
                <button
                  type="button"
                  onClick={handleAddColumn}
                  className="flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Add Column
                </button>
              </div>

              {columnsError && (
                <div className="mb-4 flex items-center text-sm text-red-600 dark:text-red-400">
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                  {columnsError}
                </div>
              )}

              <div className="space-y-3">
                {columns.map((column, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-1">
                      <select
                        value={column.name}
                        onChange={(e) => { handleColumnChange(index, 'name', e.target.value) }}
                        className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${
                          columnsError ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        required
                        disabled={!selectedTable || columnsLoading}
                      >
                        <option value="">
                          {!selectedTable 
                            ? 'Select a table first' 
                            : columnsLoading 
                            ? 'Loading columns...' 
                            : tableColumns.length === 0
                            ? 'No columns found'
                            : 'Select column'
                          }
                        </option>
                        {tableColumns
                          .filter(col => col.name)
                          .map((col) => (
                            <option key={col.name} value={col.name}>
                              {col.name}
                              {(col.type || col.dataType) ? ` (${col.type || col.dataType})` : ''}
                              {column.order === 'DESC' ? ' ↓' : ''}
                            </option>
                          ))}
                        {columnsQueryError && (
                          <option value="" disabled>
                            Error loading columns
                          </option>
                        )}
                      </select>
                    </div>
                    
                    <div className="w-24">
                      <select
                        value={column.order}
                        onChange={(e) => { handleColumnChange(index, 'order', e.target.value) }}
                        className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${
                          columnsError ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        title={column.order === 'DESC' ? 'DESC ordering (will be sent as "column DESC")' : 'ASC ordering (default)'}
                      >
                        <option value="ASC">ASC</option>
                        <option value="DESC">DESC ↓</option>
                      </select>
                    </div>

                    {columns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => { handleRemoveColumn(index) }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {columnsQueryError && (
                <div className="mt-4 flex items-center text-sm text-red-600 dark:text-red-400">
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                  Error loading columns: {columnsQueryError.message || 'Unknown error'}
                </div>
              )}
            </div>

            {/* Advanced Options */}
            {isDatabasePostgreSQL && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Advanced Options
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WHERE Clause (Partial Index)
                  </label>
                  <textarea
                    value={whereClause}
                    onChange={(e) => { handleWhereClauseChange(e.target.value) }}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm ${
                      whereClauseError 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="e.g., status = 'active' AND deleted_at IS NULL"
                  />
                  {whereClauseError && (
                    <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      {whereClauseError}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional: Create a partial index that only includes rows matching this condition
                  </p>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Index Guidelines & Validation:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>B-Tree indexes are most common and suitable for equality and range queries</li>
                    <li>Unique indexes automatically enforce uniqueness constraints</li>
                    <li>Consider the order of columns based on query patterns (ASC/DESC)</li>
                    <li>Partial indexes (WHERE clause) can save space and improve performance</li>
                    <li>Index names must be unique within the database and follow naming conventions</li>
                    <li>Use descriptive names like "idx_table_column" or "idx_table_column1_column2"</li>
                    <li>Avoid reserved words and stick to alphanumeric characters and underscores</li>
                    <li>All form fields are validated in real-time to prevent errors</li>
                    <li>Submit button is disabled until all validations pass</li>
                    {isDatabaseMySQL && (
                      <>
                        <li>MySQL supports INDEX and UNIQUE types</li>
                        <li>Column ordering: DESC columns sent as "column DESC", ASC uses default</li>
                      </>
                    )}
                    {isDatabasePostgreSQL && (
                      <>
                        <li>PostgreSQL supports multiple index types: BTREE, HASH, GIN, GIST, SP-GIST, BRIN</li>
                        <li>GIN indexes are great for arrays, JSONB, and full-text search</li>
                        <li>Hash indexes do not support DESC ordering</li>
                        <li>GIN indexes cannot be unique</li>
                        <li>Concurrent creation avoids table locking but takes longer</li>
                        <li>Column ordering: DESC columns sent as "column DESC", ASC uses default</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Form Validation Summary */}
            {(indexNameError || tableError || columnsError || whereClauseError || Object.keys(formErrors).length > 0) && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <p className="font-medium mb-2">Please fix the following validation errors:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      {indexNameError && <li>Index Name: {indexNameError}</li>}
                      {tableError && <li>Table: {tableError}</li>}
                      {columnsError && <li>Columns: {columnsError}</li>}
                      {whereClauseError && <li>WHERE Clause: {whereClauseError}</li>}
                      {Object.entries(formErrors).map(([field, error]) => (
                        <li key={field}>{field === 'indexType' ? 'Index Type' : field}: {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onIndexCreated}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !!indexNameError || !!tableError || !!columnsError || !!whereClauseError || Object.keys(formErrors).length > 0}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create Index'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
